import React, { useMemo, useState } from 'react';
import {
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { invitationsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { Player } from '../../types/player';
import { matchesSearch } from '../../utils/search';
import { useBatchedList } from '../../hooks/useBatchedList';
import LazyLoadTrigger from '../ui/LazyLoadTrigger';
import type {
  BulkInvitationItem,
  BulkInvitationResult,
} from '../../types/invitation';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

interface BulkInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  players: Player[];
}

/** Egy kiküldendő meghívó sor. Egy játékoshoz több is tartozhat. */
interface InviteRow {
  key: string;
  email: string;
  isParent: boolean;
}

let rowCounter = 0;
const nextRowKey = () => `row-${++rowCounter}`;

const ADULT_AGE = 18;

/** Nagykorú játékosnak alapból saját fiókot, kiskorúnak szülőit ajánlunk. */
const defaultIsParent = (player: Player): boolean => {
  if (!player.birth) return true;

  const birth = new Date(player.birth);
  if (Number.isNaN(birth.getTime())) return true;

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const hadBirthday =
    now.getMonth() > birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() >= birth.getDate());
  if (!hadBirthday) age -= 1;

  return age < ADULT_AGE;
};

const hasOwnAccount = (player: Player) => player.userId != null;
const parentCount = (player: Player) =>
  (player.parentUserId != null ? 1 : 0) + (player.secondParentUserId != null ? 1 : 0);
const hasAnyAccount = (player: Player) => hasOwnAccount(player) || parentCount(player) > 0;

/**
 * Tömeges meghívás a játékos listáról. Alapból azokat a játékosokat kínálja
 * fel, akikhez egyetlen felhasználó sincs kapcsolva.
 *
 * A létrejövő fiók automatikusan a játékoshoz kötődik - saját fiókként vagy
 * szülőként, soronként választhatóan. A kapcsolást a meghívó elfogadásakor a
 * backend végzi el (a meghívóban tárolt playerId + szerepkör alapján).
 */
const BulkInviteModal: React.FC<BulkInviteModalProps> = ({ isOpen, onClose, players }) => {
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [includeWithParent, setIncludeWithParent] = useState(false);
  const [rowsByPlayer, setRowsByPlayer] = useState<Record<number, InviteRow[]>>({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<BulkInvitationResult | null>(null);

  const ageGroups = useMemo(() => {
    const names = new Set<string>();
    players.forEach((p) => {
      (p.ageGroups?.length ? p.ageGroups : [p.ageGroup]).forEach((g) => g && names.add(g));
    });
    return Array.from(names).sort();
  }, [players]);

  const candidates = useMemo(() => {
    return players
      .filter((p) => (includeWithParent ? !hasOwnAccount(p) : !hasAnyAccount(p)))
      .filter((p) => matchesSearch(search, p.name))
      .filter((p) =>
        ageGroup
          ? (p.ageGroups && p.ageGroups.includes(ageGroup)) || p.ageGroup === ageGroup
          : true
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [players, search, ageGroup, includeWithParent]);

  const rowsFor = (player: Player): InviteRow[] =>
    rowsByPlayer[player.id] ?? [{ key: `initial-${player.id}`, email: '', isParent: defaultIsParent(player) }];

  const updateRows = (playerId: number, updater: (rows: InviteRow[]) => InviteRow[]) => {
    setRowsByPlayer((current) => {
      const player = players.find((p) => p.id === playerId)!;
      const existing = current[playerId] ?? [
        { key: `initial-${playerId}`, email: '', isParent: defaultIsParent(player) },
      ];
      return { ...current, [playerId]: updater(existing) };
    });
  };

  // A kiküldendő meghívók MINDEN beírt címből állnak össze, nem csak a most
  // látható szűrésből - különben korosztályváltás után a korábban kitöltöttek
  // csendben kimaradnának a küldésből.
  const items: BulkInvitationItem[] = useMemo(() => {
    const known = new Set(players.map((p) => p.id));
    return Object.entries(rowsByPlayer).flatMap(([playerId, rows]) =>
      known.has(Number(playerId))
        ? rows
            .filter((row) => row.email.trim().length > 0)
            .map((row) => ({ playerId: Number(playerId), email: row.email.trim(), isParent: row.isParent }))
        : []
    );
  }, [players, rowsByPlayer]);

  const visibleIds = useMemo(() => new Set(candidates.map((p) => p.id)), [candidates]);
  const hiddenFilledCount = items.filter((i) => !visibleIds.has(i.playerId)).length;

  // 800 játékosnál egyszerre csak egy adag soron van beviteli mező
  const { visible: visibleCandidates, hasMore, loadMore } = useBatchedList(candidates, 40);

  const handleClose = () => {
    setResult(null);
    setRowsByPlayer({});
    setSearch('');
    setAgeGroup('');
    onClose();
  };

  const handleSend = async () => {
    if (items.length === 0) return;

    setSending(true);
    try {
      const response = await invitationsAPI.createBulk({ items });
      setResult(response);

      if (response.failedCount === 0) {
        showToast(`${response.createdCount} meghívó kiküldve.`, 'success');
      } else {
        showToast(
          `${response.createdCount} meghívó kiment, ${response.failedCount} nem sikerült.`,
          'info'
        );
      }

      // A játékos listát szándékosan NEM töltjük újra: a kiküldés még nem
      // változtat a játékos-felhasználó kapcsolaton (az csak elfogadáskor jön
      // létre), a szülő PlayersList újratöltése viszont lecsatolná ezt a
      // modalt a töltés-jelző miatt - és vele az itteni eredménylistát is.

      // A sikeres sorokat kivesszük, hogy újraküldéskor ne menjenek ki mégegyszer
      const failedEmails = new Set(
        response.results.filter((r) => !r.success).map((r) => r.email.toLowerCase())
      );
      setRowsByPlayer((current) => {
        const next: Record<number, InviteRow[]> = {};
        Object.entries(current).forEach(([playerId, rows]) => {
          next[Number(playerId)] = rows.map((row) =>
            row.email && !failedEmails.has(row.email.trim().toLowerCase())
              ? { ...row, email: '' }
              : row
          );
        });
        return next;
      });
    } catch (err) {
      showToast(getErrorMessage(err, 'A meghívókat nem sikerült kiküldeni.'), 'error');
    } finally {
      setSending(false);
    }
  };

  useEscapeToClose(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={'Meghívók kiküldése'}>
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />

      <div className="relative z-50 flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 p-4 md:p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Meghívók kiküldése</h2>
            <p className="mt-1 text-sm text-gray-500">
              Azok a játékosok, akikhez még nincs felhasználó kapcsolva. A meghívó elfogadásakor
              a fiók automatikusan ehhez a játékoshoz kötődik.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 text-gray-400 hover:text-gray-600"
            aria-label="Bezárás"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 md:flex-row md:items-center md:p-6">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név szerint"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 md:max-w-xs"
          />
          <select
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 md:w-auto"
          >
            <option value="">Minden korosztály</option>
            {ageGroups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeWithParent}
              onChange={(e) => setIncludeWithParent(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Azok is, akiknek már van szülői fiókja
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nincs olyan játékos, akihez ne tartozna felhasználó.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visibleCandidates.map((player) => {
                const rows = rowsFor(player);
                const parents = parentCount(player);

                return (
                  <li key={player.id} className="py-4 first:pt-0">
                    <div className="mb-2 flex flex-wrap items-baseline gap-2">
                      <span className="font-medium text-gray-900">{player.name}</span>
                      <span className="text-xs text-gray-500">
                        {player.ageGroups?.length ? player.ageGroups.join(', ') : player.ageGroup}
                      </span>
                      {parents > 0 && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {parents === 1 ? 'Egy szülői fiók már van' : 'Két szülői fiók már van'}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      {rows.map((row, index) => (
                        <div key={row.key} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <input
                            type="email"
                            value={row.email}
                            onChange={(e) =>
                              updateRows(player.id, (current) =>
                                current.map((r) =>
                                  r.key === row.key ? { ...r, email: e.target.value } : r
                                )
                              )
                            }
                            placeholder="email@pelda.hu"
                            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                          />
                          <select
                            value={row.isParent ? 'parent' : 'self'}
                            onChange={(e) =>
                              updateRows(player.id, (current) =>
                                current.map((r) =>
                                  r.key === row.key
                                    ? { ...r, isParent: e.target.value === 'parent' }
                                    : r
                                )
                              )
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:w-40"
                          >
                            <option value="parent">Szülő fiók</option>
                            <option value="self">Saját fiók</option>
                          </select>
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                updateRows(player.id, (current) =>
                                  current.filter((r) => r.key !== row.key)
                                )
                              }
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-600"
                              aria-label="Email cím törlése"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                          {index === rows.length - 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                updateRows(player.id, (current) => [
                                  ...current,
                                  { key: nextRowKey(), email: '', isParent: true },
                                ])
                              }
                              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Cím
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {hasMore && candidates.length > 0 && (
            <LazyLoadTrigger key={visibleCandidates.length} onLoadMore={loadMore} />
          )}

          {result && (
            <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Eredmény</h3>
              <ul className="mt-2 space-y-1 text-sm">
                {result.results.map((outcome, index) => (
                  <li
                    key={`${outcome.email}-${index}`}
                    className={outcome.success ? 'text-gray-700' : 'text-red-700'}
                  >
                    <span className="font-medium">{outcome.playerName}</span> · {outcome.email} ·{' '}
                    {outcome.isParent ? 'szülő' : 'saját'} —{' '}
                    {outcome.success
                      ? outcome.emailSent
                        ? 'kiküldve'
                        : 'meghívó elkészült, de az email nem ment ki'
                      : outcome.error}
                  </li>
                ))}
              </ul>
              {result.results.some((o) => o.success && !o.emailSent) && (
                <p className="mt-3 flex items-start gap-2 text-xs text-amber-700">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  Ahol az email nem ment ki, a meghívó akkor is érvényes - a linkje a
                  Felhasználók oldal Meghívók listájában érhető el.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between md:p-6">
          <p className="text-sm text-gray-500">
            {items.length === 0
              ? 'Adj meg legalább egy email címet.'
              : `${items.length} meghívó kiküldésre kész.`}
            {hiddenFilledCount > 0 && ` Ebből ${hiddenFilledCount} a jelenlegi szűrésen kívül van.`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Bezárás
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || items.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <EnvelopeIcon className="h-4 w-4" />
              {sending ? 'Küldés...' : 'Meghívók kiküldése'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInviteModal;
